import React, { useState } from 'react';
import {
    Modal,
    Box,
    TextField,
    Typography,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Button,
    Chip
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import CloseIcon from '@material-ui/icons/Close';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
    modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    modalContent: {
        width: 760,
        maxWidth: 'calc(100% - 32px)',
        maxHeight: 'calc(100vh - 32px)',
        backgroundColor: theme.palette.background.paper,
        borderRadius: theme.spacing(1.5),
        boxShadow: theme.shadows[12],
        outline: 'none',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        [theme.breakpoints.down('xs')]: {
            width: 'calc(100% - 16px)',
            maxWidth: 'none',
            maxHeight: 'calc(100vh - 16px)',
            borderRadius: theme.spacing(1),
        },
    },
    modalHeader: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        padding: theme.spacing(2, 2, 1.5),
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    headerTitle: {
        fontWeight: 600,
    },
    headerSubtitle: {
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(0.5),
    },
    closeButton: {
        marginTop: theme.spacing(-0.75),
        marginRight: theme.spacing(-0.75),
    },
    modalBody: {
        overflowY: 'auto',
    },
    searchContainer: {
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(2, 2, 1.5),
    },
    searchInput: {
        marginLeft: theme.spacing(1),
        flex: 1,
    },
    templateList: {
        padding: theme.spacing(0, 2, 2),
    },
    templateItem: {
        display: 'block',
        backgroundColor: theme.palette.optionsBackground || theme.palette.background.default,
        color: theme.palette.text.primary,
        padding: theme.spacing(2),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
        marginBottom: theme.spacing(1.5),
        cursor: 'pointer',
        transition: 'border-color 160ms ease, box-shadow 160ms ease, transform 80ms ease',
        '&:hover, &:focus-within': {
            borderColor: theme.palette.primary.main,
            boxShadow: theme.shadows[2],
        },
        '&:active': {
            transform: 'translateY(1px)',
        },
        '&:last-child': {
            marginBottom: 0,
        },
    },
    templateInfo: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
    },
    templateName: {
        fontWeight: 600,
        wordBreak: 'break-word',
    },
    badges: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(0.75),
    },
    previewLabel: {
        display: 'block',
        color: theme.palette.text.secondary,
        fontWeight: 500,
        marginTop: theme.spacing(1.5),
        marginBottom: theme.spacing(0.5),
    },
    templatePreview: {
        color: theme.palette.text.secondary,
        whiteSpace: 'pre-line',
        overflow: 'hidden',
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 3,
        wordBreak: 'break-word',
    },
    templateActions: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: theme.spacing(1.5),
    },
    selectButton: {
        minWidth: 150,
    },
    emptyState: {
        padding: theme.spacing(4, 2),
        textAlign: 'center',
        color: theme.palette.text.secondary,
        border: `1px dashed ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
    },
    detailsContent: {
        padding: theme.spacing(2),
    },
    detailsCard: {
        marginTop: theme.spacing(1.5),
        padding: theme.spacing(2),
        backgroundColor: theme.palette.optionsBackground || theme.palette.background.default,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
    },
    detailsPreview: {
        marginTop: theme.spacing(1.5),
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        color: theme.palette.text.secondary,
    },
    detailsActions: {
        display: 'flex',
        gap: theme.spacing(1),
        marginTop: theme.spacing(2),
        [theme.breakpoints.down('xs')]: {
            flexDirection: 'column-reverse',
        },
    },
    actionButton: {
        flex: 1,
    },
    category: {
        marginTop: theme.spacing(1),
    },
}));

const TemplateModal = ({ open, handleClose, templates, onSelectTemplate }) => {
    const classes = useStyles();
    const [search, setSearch] = useState('');
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [variables, setVariables] = useState([]);
    const [variableValues, setVariableValues] = useState({});
    const [renderedContent, setRenderedContent] = useState('');

    const handleSearchChange = (event) => {
        setSearch(event.target.value);
    };

    const extractVariablesByComponent = (components) => {
        const regex = /{{(\d+)}}/g;
        let variables = {
            header: [],
            body: [],
            footer: [],
            buttons: []
        };

        components.forEach((component) => {
            let match;
            const type = component.type?.toLowerCase();
            const text = component.text || '';

            if (type === 'header' && ['IMAGE', 'VIDEO'].includes(component?.format)) {
                variables[type].push({ type: `${component?.format.toLowerCase()}`, prompt: component?.format.toLowerCase() === 'image' ? 'Insira a URL da imagem' : 'Insira a URL do vídeo' });
            }
            // Para os botões, verifique a URL para variáveis
            if (type === 'buttons') {
                const buttons = JSON.parse(component.buttons)
                buttons.forEach((button, index) => {
                    if (button.example) {
                        console.log("button", button, index)
                        variables[type].push({ type: button.type, prompt: button.example, index: index });
                    }
                    else if (variables[type]) {
                        while ((match = regex.exec(text)) !== null) {
                            variables[type].push(match[0]);
                        }
                    }
                });
            } else if (variables[type]) {
                while ((match = regex.exec(text)) !== null) {
                    variables[type].push({ type: 'text', prompt: match[0] });
                }
            } else {
                console.warn(`Tipo de componente desconhecido: ${type}`);
            }
        });
        return variables;
    };



    const handleSelectTemplate = (template) => {
        const components = template?.components || [];
        const extractedVariables = extractVariablesByComponent(components);

        setSelectedTemplate(template);
        setVariables(extractedVariables);
        setRenderedContent(components.map((component) => component?.text).join(`\n`));
    };

    // const generateBodyToSave = (content, variables) => {
    //     let bodyToSave = content;
    //     console.log("variables", variables)
    //     console.log("bodyToSave", bodyToSave)
    //     Object.keys(variables).forEach((componentType) => {
    //         console.log("componentType", componentType)
    //         const componentVariables = variables[componentType];
    //         console.log("componentVariables", componentVariables, typeof componentVariables)
    //         // Verificar se componentVariables é um array
    //         if (Array.isArray(componentVariables)) {
    //             componentVariables.forEach((variable, index) => {
    //                 const placeholder = `{{${index + 1}}}`;
    //                 const value = variable?.value || '';
    //                 bodyToSave = bodyToSave.replace(placeholder, value);
    //             });
    //         } else if (typeof componentVariables === 'object') {
    //             Object.keys(componentVariables).forEach((key, index) => {
    //                 const placeholder = `{{${index + 1}}}`;
    //                 const value = componentVariables[key]?.value || '';
    //                 bodyToSave = bodyToSave.replace(placeholder, value);
    //             });
    //         } else {
    //             console.error(`Expected array or object for componentType: ${componentType}, but got`, componentVariables);
    //         }
    //     });
    //     console.log("bodyToSave", bodyToSave)

    //     return bodyToSave;
    // };
    const generateBodyToSave = (content, variables) => {
        let bodyToSave = content;
        Object.keys(variables).forEach((componentType) => {
            const componentVariables = variables[componentType];

            if (Array.isArray(componentVariables)) {
                console.log("componentVariables ARRAY", componentVariables)
                // Se for um array, iteramos sobre ele substituindo as variáveis
                componentVariables.forEach((variable, index) => {
                    const placeholder = `{{${index + 1}}}`;
                    const value = variable?.value || '';
                    bodyToSave = bodyToSave.replace(placeholder, value);
                });
            } else if (typeof componentVariables === 'object') {
                console.log("componentVariables OBJECT", componentVariables)
                // Se for um objeto (caso do header com uma imagem ou outro tipo de conteúdo)
                if (componentType === 'header') {
                    console.log("componentVariables", componentVariables)
                    // Substituir um placeholder específico no header por uma URL de imagem
                    Object.keys(componentVariables).forEach((key, index) => {
                        const value = componentVariables[key]?.value || '';
                        console.log("value", value, value.startsWith("http"))
                        if (value.startsWith("http")) {                            
                            bodyToSave = `${value} \n ${bodyToSave}`
                            console.log("bodyToSave", bodyToSave)
                        } else {
                            const placeholder = `{{${index + 1}}}`;

                            bodyToSave = bodyToSave.replace(placeholder, value);
                        }
                    });
                } else {
                    // Para texto ou outros tipos de componentes, substituímos as variáveis como antes
                    Object.keys(componentVariables).forEach((key, index) => {
                        const placeholder = `{{${index + 1}}}`;
                        const value = componentVariables[key]?.value || '';
                        bodyToSave = bodyToSave.replace(placeholder, value);
                    });
                }
            } else {
                console.error(`Expected array or object for componentType: ${componentType}, but got`, componentVariables);
            }
        });

        console.log("bodyToSave", bodyToSave);
        return bodyToSave;
    };

    const handleSendTemplate = () => {
        // ✅ Validar que todas as variáveis foram preenchidas
        let hasEmpty = false;
        let emptyFields = [];
        Object.keys(variables).forEach((componentType) => {
            variables[componentType].forEach((variable, index) => {
                const val = variableValues[componentType]?.[index]?.value;
                if (!val || val.trim() === '') {
                    hasEmpty = true;
                    emptyFields.push(`${componentType.toUpperCase()} - ${variable?.prompt || `Variável ${index + 1}`}`);
                }
            });
        });

        if (hasEmpty) {
            alert(`⚠️ Preencha todas as variáveis antes de enviar:\n\n${emptyFields.join('\n')}`);
            return;
        }

        const bodyToSave = generateBodyToSave(renderedContent, variableValues);
        const templateWithVariables = {
            ...selectedTemplate,
            variables: variableValues,
            bodyToSave
        };
        onSelectTemplate(templateWithVariables);
    };

    const handleVariableChange = (componentType, index, value, buttonIndex) => {
        // Atualiza o estado das variáveis por tipo de componente
        const newComponentValues = { ...variableValues[componentType], [index]: { value, buttonIndex } };
        const newValues = { ...variableValues, [componentType]: newComponentValues };
        setVariableValues(newValues);
    };

    const filteredTemplates = templates.filter((template) =>
        template?.shortcode?.toLowerCase().includes(search?.toLowerCase())
    )

    return (
        <Modal
            open={open}
            onClose={handleClose}
            className={classes.modal}
            maxWidth="md"
            aria-labelledby="template-modal-title"
        >
            <Box className={classes.modalContent}>
                <div className={classes.modalHeader}>
                    <div>
                        <Typography variant="h6" id="template-modal-title" className={classes.headerTitle}>
                            Templates do WhatsApp
                        </Typography>
                        <Typography variant="body2" className={classes.headerSubtitle}>
                            {selectedTemplate ? 'Confira os detalhes antes de enviar' : 'Escolha um template aprovado para continuar'}
                        </Typography>
                    </div>
                    <IconButton
                        className={classes.closeButton}
                        onClick={handleClose}
                        aria-label="Fechar templates do WhatsApp"
                    >
                        <CloseIcon />
                    </IconButton>
                </div>

                <Box className={classes.modalBody}>
                    {!selectedTemplate && (
                        <>
                            <div className={classes.searchContainer}>
                                <SearchIcon color="action" />
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    placeholder="Pesquisar pelo nome do template"
                                    value={search}
                                    onChange={handleSearchChange}
                                    className={classes.searchInput}
                                    inputProps={{ 'aria-label': 'Pesquisar templates' }}
                                />
                            </div>

                            <List className={classes.templateList} disablePadding>
                                {filteredTemplates.map((template, index) => (
                                    <ListItem
                                        key={template.id || template.shortcode || index}
                                        className={classes.templateItem}
                                        onClick={() => handleSelectTemplate(template)}
                                    >
                                        <ListItemText
                                            disableTypography
                                            primary={
                                                <div className={classes.templateInfo}>
                                                    <Typography variant="body1" className={classes.templateName}>
                                                        {template.shortcode}
                                                    </Typography>
                                                    <div className={classes.badges}>
                                                        <Chip size="small" label={`Idioma: ${template.language}`} variant="outlined" />
                                                        <Chip size="small" label={template.category} variant="outlined" />
                                                    </div>
                                                </div>
                                            }
                                            secondary={
                                                <>
                                                    <Typography variant="caption" className={classes.previewLabel}>
                                                        PRÉVIA
                                                    </Typography>
                                                    <Typography variant="body2" component="div" className={classes.templatePreview}>
                                                        {template?.components?.map(component => component?.text).join(`\n`)}
                                                    </Typography>
                                                    <div className={classes.templateActions}>
                                                        <Button
                                                            type="button"
                                                            variant="contained"
                                                            color="primary"
                                                            size="small"
                                                            className={classes.selectButton}
                                                            startIcon={<CheckCircleOutlineIcon />}
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                handleSelectTemplate(template);
                                                            }}
                                                        >
                                                            Selecionar este
                                                        </Button>
                                                    </div>
                                                </>
                                            }
                                        />
                                    </ListItem>
                                ))}
                                {filteredTemplates.length === 0 && (
                                    <Box className={classes.emptyState}>
                                        <Typography variant="body1">Nenhum template encontrado</Typography>
                                        <Typography variant="body2">Tente pesquisar por outro nome.</Typography>
                                    </Box>
                                )}
                            </List>
                        </>
                    )}
                    {selectedTemplate && (
                        <Box className={classes.detailsContent}>
                            <Typography variant="h6">Template selecionado</Typography>
                            <Box className={classes.detailsCard}>
                                <div className={classes.templateInfo}>
                                    <Typography variant="body1" className={classes.templateName}>
                                        {selectedTemplate.name || selectedTemplate.shortcode}
                                    </Typography>
                                    <div className={classes.badges}>
                                        <Chip size="small" label={`Idioma: ${selectedTemplate.language}`} variant="outlined" />
                                        <Chip size="small" label={selectedTemplate.category} variant="outlined" />
                                    </div>
                                </div>
                                <Typography variant="body2" className={classes.detailsPreview}>
                                    {renderedContent}
                                </Typography>
                            </Box>
                            {/* Renderização dos campos para preencher as variáveis */}
                            {Object.keys(variables).map((componentType, index) => (
                                <div key={`${componentType}-${index}`}>
                                    {variables[componentType].length > 0 && (
                                        <>
                                            <Typography variant="h6">{componentType.toUpperCase()}</Typography>
                                            {variables[componentType].map((variable, index) => (
                                                <TextField
                                                    key={`${componentType}-${index}`}
                                                    label={`${variable?.prompt} *`}
                                                    placeholder={`Preencha o valor para ${variable?.prompt}`}
                                                    value={variableValues[componentType]?.[index]?.value || ''}
                                                    onChange={(e) => handleVariableChange(componentType, index, e.target.value, variable?.index || 0)}
                                                    fullWidth
                                                    margin="normal"
                                                    required
                                                    error={variableValues[componentType]?.[index]?.value === ''}
                                                    helperText={!variableValues[componentType]?.[index]?.value ? 'Campo obrigatório' : ''}
                                                />
                                            ))}
                                        </>
                                    )}
                                </div>
                            ))}
                            <div className={classes.detailsActions}>
                                <Button
                                    variant="outlined"
                                    className={classes.actionButton}
                                    onClick={() => {
                                        setSelectedTemplate(null);
                                        setVariableValues({});
                                        setRenderedContent('');
                                    }}
                                >
                                    Voltar
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    className={classes.actionButton}
                                    onClick={handleSendTemplate}
                                >
                                    Enviar Template
                                </Button>
                            </div>
                        </Box>
                    )}
                </Box>
            </Box>
        </Modal>
    );
};

export default TemplateModal;
